/**
 * The screen registry. The top bar is rendered from this list, and `go()`
 * looks up the component by `kind`.
 *
 * To add a screen: write `src/<name>/<Name>Screen.tsx` exporting a component
 * that takes `{ params }` and reads everything else from `useApp()`, add one
 * entry here in number order, and give it a path in `routes.ts`.
 */

import type { ComponentType } from "react";
import type { FeatureId } from "@baros/core";
import type { IconName } from "./icons.js";
import type { Me } from "./api.js";
import { BooksScreen } from "./books/BooksScreen.js";
import { CashupScreen } from "./cashup/CashupScreen.js";
import { ChatScreen } from "./ChatScreen.js";
import type { ScreenState } from "./context.js";
import { HOME } from "./routes.js";
import { DashboardScreen } from "./dashboard/DashboardScreen.js";
import { DrinksScreen } from "./drinks/DrinksScreen.js";
import { ExpensesScreen } from "./expenses/ExpensesScreen.js";
import { GuestsScreen } from "./guests/GuestsScreen.js";
import { QueueScreen } from "./guests/QueueScreen.js";
import { MenuScreen } from "./menu/MenuScreen.js";
import { OrdersScreen } from "./orders/OrdersScreen.js";
import { OrgScreen } from "./org/OrgScreen.js";
import { PlatformScreen } from "./platform/PlatformScreen.js";
import { PrepScreen } from "./prep/PrepScreen.js";
import { RecipesScreen } from "./RecipesScreen.js";
import { SalesScreen } from "./sales/SalesScreen.js";
import { TrainingScreen } from "./training/TrainingScreen.js";
import { StockScreen } from "./stock/StockScreen.js";
import { SuppliersScreen } from "./suppliers/SuppliersScreen.js";
import { WastageScreen } from "./wastage/WastageScreen.js";
import { WhatTailScreen } from "./whattail/WhatTailScreen.js";
import { WheelScreen } from "./wheel/WheelScreen.js";

export interface ScreenProps {
  readonly params: NonNullable<ScreenState["params"]>;
}

export interface ScreenDef {
  /** The `kind` used in `go({ kind })`. */
  readonly kind: string;
  /** The prototype's screen number, kept for the figure captions: `02`. */
  readonly number: string;
  /** What the nav shows in place of the number. */
  readonly icon: IconName;
  readonly label: string;
  readonly component: ComponentType<ScreenProps>;
  /** Hide from the nav for people who may not use it. Defaults to visible. */
  readonly visible?: (me: Me) => boolean;
  /** The feature it belongs to (D14); hidden whenever the organisation does not have it. */
  readonly feature?: FeatureId;
}

export const SCREENS: readonly ScreenDef[] = [
  // Venox's own operators, not a customer screen; hidden from everyone else.
  { kind: "platform", number: "00", icon: "platform", label: "Platform", component: PlatformScreen, visible: (me) => me.platform },
  { kind: "dash", number: "01", icon: "dashboard", label: "Dashboard", component: DashboardScreen },
  // Items (#180): the drinks and food, each with its recipes. Still "recipes" in the address.
  { kind: "recipes", number: "02", icon: "recipes", label: "Items", component: RecipesScreen },
  // The same book dealt out as cards, for behind the bar. Service reaches
  // the drinks this way; Items above is the manager's table.
  { kind: "drinks", number: "02", icon: "recipes", label: "Drinks", component: DrinksScreen },
  { kind: "wheel", number: "03", icon: "wheel", label: "Flavour wheel", component: WheelScreen, feature: "wheel" },
  { kind: "chat", number: "04", icon: "chat", label: "Development chat", component: ChatScreen, visible: (me) => me.can["chat:use"], feature: "chat" },
  { kind: "count", number: "05", icon: "stock", label: "Stocktake", component: StockScreen, feature: "stocktake" },
  { kind: "prep", number: "06", icon: "prep", label: "Prep", component: PrepScreen, feature: "prep" },
  { kind: "menu", number: "07", icon: "menu", label: "Menu engineering", component: MenuScreen, feature: "menu" },
  { kind: "waste", number: "08", icon: "wastage", label: "Wastage", component: WastageScreen, visible: (me) => me.can["wastage:log"], feature: "wastage" },
  { kind: "sup", number: "09", icon: "suppliers", label: "Suppliers", component: SuppliersScreen, feature: "suppliers" },
  { kind: "order", number: "10", icon: "orders", label: "Orders", component: OrdersScreen, visible: (me) => me.can["order:create"], feature: "orders" },
  { kind: "exp", number: "11", icon: "expenses", label: "Expenses", component: ExpensesScreen, visible: (me) => me.can["expense:manage"], feature: "expenses" },
  { kind: "cashup", number: "13", icon: "cashup", label: "Cash-up", component: CashupScreen, visible: (me) => me.can["cashup:record"], feature: "cashup" },
  // What the till took (D12). Whoever cashes up may read it; connecting Square is screen 12's.
  { kind: "sales", number: "15", icon: "sales", label: "Sales", component: SalesScreen, visible: (me) => me.can["cashup:record"], feature: "square" },
  { kind: "org", number: "12", icon: "org", label: "Organisation", component: OrgScreen, visible: (me) => me.can["org:manage"] },
  // The door (#126): whoever runs the queue. Guests (#127): whoever looks after them.
  { kind: "queue", number: "16", icon: "queue", label: "Door queue", component: QueueScreen, visible: (me) => me.can["queue:run"], feature: "queue" },
  // The questionnaire a bartender runs with a guest: three drinks off tonight's list, no AI.
  { kind: "whattail", number: "18", icon: "whattail", label: "WhatTail", component: WhatTailScreen, feature: "whattail" },
  { kind: "guests", number: "17", icon: "guests", label: "Guests", component: GuestsScreen, visible: (me) => me.can["guest:manage"], feature: "guests" },
  // The development chat's reference shelf (D11): managers, owners and admins.
  { kind: "books", number: "14", icon: "books", label: "Books", component: BooksScreen, visible: (me) => me.can["books:manage"], feature: "books" },
  // What the bar teaches its own staff (#190). Everyone works through it;
  // writing it takes `training:manage`, which the screen offers in place.
  { kind: "training", number: "19", icon: "training", label: "Training", component: TrainingScreen, feature: "training" },
];

export const DEFAULT_SCREEN: ScreenState = HOME;

export { NAV_GROUPS, type NavGroup, type NavGroupId } from "./nav-groups.js";
